/**
 * Расчёт калорий и БЖУ на стороне webapp.
 *
 * ВАЖНО: формулы ЗЕРКАЛЯТ backend `utils/helpers.py` один-в-один.
 * Если меняешь здесь — обязательно синхронизируй в боте, и наоборот.
 * Причина дублирования: `aisha-v2.service` пока не возвращает расчёт,
 * а ждать ответа от бота через `tg.sendData()` невозможно — это fire-and-forget.
 * Поэтому webapp считает локально и сразу показывает брифинг, а бот
 * параллельно считает то же самое и сохраняет в БД для истории.
 *
 * Источник: `utils/helpers.py::calculate_bmr / calculate_tdee /
 * calculate_target_calories / calculate_macros` (от 2026-02-09, рекомендации 2025-2030).
 */

export type NutritionGender = 'male' | 'female';

/**
 * Клиентские коды активности — 1:1 с `utils/helpers.py::parse_activity_choice`.
 * 5 уровней, точно как в FSM-анкете старого бота, чтобы расчёты совпадали байт-в-байт.
 */
export type NutritionActivity = 'minimal' | 'low' | 'medium' | 'high' | 'very_high';

/** Клиентские коды цели — те же, что в `OnboardingScreen`. */
export type NutritionGoal =
    | 'weight_loss'
    | 'muscle_gain'
    | 'maintenance'
    | 'medical';

export interface NutritionInput {
    gender: NutritionGender;
    age: number;
    weight: number; // кг
    height: number; // см
    activity: NutritionActivity;
    goal: NutritionGoal;
    /** Подтверждённый целевой вес из анкеты (кг). Если ниже текущего — мягкий дефицит 10%. */
    targetWeight?: number;
}

export interface NutritionMacros {
    protein: number;
    fat: number;
    carbs: number;
}

export interface NutritionResult {
    bmr: number;
    tdee: number;
    calories: number;
    macros: NutritionMacros;
}

// Mifflin-St Jeor.
// Math.floor (не round) — зеркалит Python int() (усечение) в calculate_bmr,
// чтобы число совпадало с бэкендом байт-в-байт (иначе рассинхрон ±1 ккал).
export function calculateBmr(input: Pick<NutritionInput, 'gender' | 'age' | 'weight' | 'height'>): number {
    const { gender, age, weight, height } = input;
    const base = 10 * weight + 6.25 * height - 5 * age;
    return Math.floor(gender === 'male' ? base + 5 : base - 161);
}

// Те же множители, что в `utils/helpers.py::calculate_tdee`.
const ACTIVITY_MULTIPLIER: Record<NutritionActivity, number> = {
    minimal:   1.2,
    low:       1.375,
    medium:    1.55,
    high:      1.725,
    very_high: 1.9,
};

export function calculateTdee(bmr: number, activity: NutritionActivity): number {
    return Math.floor(bmr * (ACTIVITY_MULTIPLIER[activity] ?? 1.2));
}

// Коэффициенты целевых калорий совпадают с `calculate_target_calories` после
// нормализации goal в боте (weight_loss→slimming, muscle_gain→mass и т.д.).
// МЕД-БЕЗОПАСНОСТЬ (2026-07-04): medical = поддержание (1.0), НЕ дефицит.
// «Здоровье» — это не похудение. Зеркалит фикс в utils/helpers.py.
const GOAL_CALORIE_RATIO: Record<NutritionGoal, number> = {
    weight_loss: 0.90, // slimming
    muscle_gain: 1.15, // mass
    maintenance: 1.0,
    medical:     1.0,  // было 0.95 (дефицит) — убрано, см. helpers.py
};

export function calculateTargetCalories(tdee: number, goal: NutritionGoal): number {
    return Math.floor(tdee * (GOAL_CALORIE_RATIO[goal] ?? 1.0));
}

/**
 * Целевые калории с мед-предохранителями.
 * Зеркалит backend `utils/helpers.py::calculate_target_calories_safe` — порядок правил
 * и округления (Math.floor = Python int()) ОБЯЗАНЫ совпадать байт-в-байт:
 * 1. ИМТ < 18.5 — никакого дефицита, target не ниже TDEE.
 * 2. Целевой вес ниже текущего при цели maintenance/medical → мягкий дефицит 10%.
 * 3. Пол калорий: не ниже 1200 (жен.) / 1500 (муж.), но никогда выше TDEE.
 */
export function calculateTargetCaloriesSafe(
    tdee: number,
    goal: NutritionGoal,
    weight?: number,
    height?: number,
    targetWeight?: number,
    gender?: NutritionGender,
): number {
    let base = calculateTargetCalories(tdee, goal);
    // 1) ИМТ-предохранитель
    if (weight && height && height > 0) {
        const bmi = weight / Math.pow(height / 100, 2);
        if (bmi < 18.5) {
            return Math.max(base, tdee); // никакого дефицита при недоборе веса
        }
    }
    // 2) Целевой вес ниже текущего → мягкий дефицит 10% для целей поддержания
    //    (helpers.py: goal in ('maintenance','medical','postpartum'); postpartum на фронте нет)
    if (
        targetWeight && weight && targetWeight < weight &&
        (goal === 'maintenance' || goal === 'medical')
    ) {
        base = Math.min(base, Math.floor(tdee * 0.90));
    }
    // 3) Пол калорий по полу (не выше TDEE)
    if (gender === 'female') {
        base = Math.max(base, Math.min(1200, tdee));
    } else if (gender === 'male') {
        base = Math.max(base, Math.min(1500, tdee));
    }
    return base;
}

// Зеркало `calculate_macros` (ветка "с весом"): белок по г/кг, углеводы по %,
// жиры — из оставшихся калорий с минимумом 0.8 г/кг.
const PROTEIN_PER_KG: Record<NutritionGoal, number> = {
    weight_loss: 1.6,
    muscle_gain: 1.6,
    maintenance: 1.2,
    medical:     1.2,
};

const CARB_PERCENT: Record<NutritionGoal, number> = {
    weight_loss: 0.25,
    muscle_gain: 0.35,
    maintenance: 0.30,
    medical:     0.30,
};

export function calculateMacros(calories: number, goal: NutritionGoal, weight: number): NutritionMacros {
    const pk = PROTEIN_PER_KG[goal] ?? 1.2;
    const protein = Math.max(0, Math.floor(weight * pk));
    const proteinKcal = protein * 4;

    const cp = CARB_PERCENT[goal] ?? 0.30;
    let carbs = Math.floor((calories * cp) / 4);
    const carbsKcal = carbs * 4;

    const fatKcal = calories - proteinKcal - carbsKcal;
    let fat = fatKcal > 0 ? Math.floor(fatKcal / 9) : Math.floor(weight * 0.8);

    // Минимумы — как в боте.
    const fatMin = Math.floor(weight * 0.8);
    if (fat < fatMin) fat = fatMin;
    if (carbs < 100) carbs = 100;

    return { protein, fat, carbs };
}

/** Одна точка входа — на случай будущих экранов. */
export function computeNutrition(input: NutritionInput): NutritionResult {
    const bmr = calculateBmr(input);
    const tdee = calculateTdee(bmr, input.activity);
    // Предохранители: ИМТ<18.5 без дефицита; целевой вес → мягкий дефицит 10%;
    // пол калорий 1200/1500 (см. helpers.py — менять синхронно!).
    const calories = calculateTargetCaloriesSafe(
        tdee, input.goal, input.weight, input.height, input.targetWeight, input.gender,
    );
    const macros = calculateMacros(calories, input.goal, input.weight);
    return { bmr, tdee, calories, macros };
}
